import { inject, Injectable, signal } from '@angular/core';
import { Analytics, setAnalyticsCollectionEnabled, setConsent } from '@angular/fire/analytics';

const STORAGE_KEY = 'analytics_consent';

export type ConsentStatus = 'granted' | 'denied' | 'pending';

@Injectable({ providedIn: 'root' })
export class AnalyticsConsentService {
  private readonly analytics = inject(Analytics, { optional: true });
  private readonly _status = signal<ConsentStatus>(this.readFromStorage());

  readonly status = this._status.asReadonly();

  get isGranted(): boolean {
    return this._status() === 'granted';
  }

  constructor() {
    // Restore SDK state from previous session
    const stored = this._status();
    if (stored === 'granted') {
      this.enableSdk();
    }
  }

  grant(): void {
    this.persist('granted');
    this.enableSdk();
  }

  deny(): void {
    this.persist('denied');
    this.disableSdk();
  }

  private enableSdk(): void {
    if (!this.analytics) {
      return;
    }
    setConsent({ analytics_storage: 'granted' });
    setAnalyticsCollectionEnabled(this.analytics, true);
  }

  private disableSdk(): void {
    if (!this.analytics) {
      return;
    }
    setConsent({ analytics_storage: 'denied' });
    setAnalyticsCollectionEnabled(this.analytics, false);
  }

  private persist(value: ConsentStatus): void {
    this._status.set(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // localStorage may be unavailable (SSR, private browsing quota)
    }
  }

  private readFromStorage(): ConsentStatus {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'granted' || stored === 'denied') {
        return stored;
      }
    } catch {
      // localStorage unavailable
    }
    return 'pending';
  }
}
